import type { RouteLocationNormalizedLoaded } from 'vue-router'
import { resolveRoutePath } from '@/utils'
import type { Menu } from '@/types/global'

/** 统一菜单路径格式，避免 Windows 下 path.resolve 与 activeMenu 不一致 */
export function normalizeMenuPath(routePath: string): string {
  if (!routePath) {
    return ''
  }
  return routePath.replace(/\\/g, '/').replace(/\/{2,}/g, '/')
}

/** hidden 子路由（-upload / -detail）推断对应列表页路径 */
export function inferHiddenRouteListPath(path: string): string | undefined {
  const normalized = normalizeMenuPath(path)
  if (!normalized) {
    return undefined
  }

  const segments = normalized.split('/').filter(Boolean)
  const lastSegment = segments[segments.length - 1]
  if (!lastSegment) {
    return undefined
  }

  const listSegment = lastSegment.replace(/-(upload|detail)$/, '')
  if (listSegment === lastSegment) {
    return undefined
  }

  segments[segments.length - 1] = listSegment
  return `/${segments.join('/')}`
}

/** 解析侧栏应高亮的路径：优先 meta.activeMenu，hidden 页回退到列表路径 */
export function resolveSidebarActivePath(route: RouteLocationNormalizedLoaded): string {
  const metaActiveMenu = typeof route.meta.activeMenu === 'string'
    ? route.meta.activeMenu.trim()
    : ''
  if (metaActiveMenu) {
    return normalizeMenuPath(metaActiveMenu)
  }

  const inferredListPath = inferHiddenRouteListPath(route.path)
  if (inferredListPath) {
    return inferredListPath
  }

  return normalizeMenuPath(route.path)
}

function hasVisibleChildren(item: Menu.recordRaw): boolean {
  if (!item.children?.length) {
    return false
  }
  return !item.children.every(child => child.meta?.sidebar === false || child.meta?.hidden === true)
}

/** 在菜单树中查找与目标路径匹配的 el-menu-item index */
export function findSidebarMenuIndexPath(
  menus: Menu.recordRaw[],
  targetPath: string,
  rootPath = '',
): string | undefined {
  const normalizedTarget = normalizeMenuPath(targetPath)

  for (const item of menus) {
    if (item.meta?.sidebar === false || item.meta?.hidden) {
      continue
    }

    const itemPath = normalizeMenuPath(resolveRoutePath(rootPath, item.path))

    if (hasVisibleChildren(item)) {
      const childMatch = findSidebarMenuIndexPath(item.children ?? [], normalizedTarget, itemPath)
      if (childMatch) {
        return childMatch
      }
      continue
    }

    const menuIndex = normalizeMenuPath(resolveRoutePath(rootPath, item.path || '', item, 'inner'))
    if (menuIndex === normalizedTarget) {
      return menuIndex
    }
  }

  return undefined
}
